import React, { useMemo, useState, useEffect } from 'react';
import { Box, Card, Grid, Typography, Divider } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import KpiCards from './KpiCards';
import FilterBarAcuAgentes, { aplicarFiltros } from './FilterBarAcuAgentes';
import { procesarDatosAcuAgentes, fmtSeg, fmtPct } from '../helpers/procesarAcuAgentes';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  ChartTooltip,
  Legend,
  Filler,
  ChartDataLabels,
);

/* ---- Estilos reutilizables ---- */
const chartBox = (dk) => ({
  border: dk ? '1px solid #3d4a62' : '1px solid #e0e0e0',
  borderRadius: '12px',
  boxShadow: dk ? '0 2px 8px rgba(0,0,0,0.2)' : '0 2px 8px rgba(0,0,0,0.04)',
  p: 2,
  background: dk ? 'rgba(31,42,64,0.5)' : 'rgba(255,255,255,0.5)',
});

const ttStyle = (dk) => ({
  backgroundColor: dk ? '#2d3a52' : '#fff',
  titleColor: dk ? '#e0e0e0' : '#2c3e50',
  bodyColor: dk ? '#b0bec5' : '#5f6368',
  borderColor: dk ? '#3d4a62' : '#e0e0e0',
  borderWidth: 1,
  padding: 12,
  displayColors: true,
});

const gridColor = (dk) => (dk ? '#3d4a62' : '#e0e0e0');
const tickColor = (dk) => (dk ? '#b0bec5' : '#7f8c8d');
const titleColor = (dk) => (dk ? '#e0e0e0' : '#2c3e50');

const SectionTitle = ({ children, isDarkMode }) => (
  <Typography
    sx={{ color: titleColor(isDarkMode), fontWeight: 700, fontSize: 14, mb: 1 }}
  >
    {children}
  </Typography>
);

/* ========================================================================= */
/*  CHARTS                                                                   */
/* ========================================================================= */

/** Distribución de tiempo por intervalo – Stacked Bar */
const ChartTiempo = ({ data, isDarkMode: dk }) => {
  if (!data) return null;
  const cfg = {
    labels: data.labels,
    datasets: [
      { label: 'Connect', data: data.connect, backgroundColor: '#3b82f6', stack: 'a' },
      { label: 'Avail', data: data.avail, backgroundColor: '#22c55e', stack: 'a' },
      { label: 'Hold', data: data.hold, backgroundColor: '#f59e0b', stack: 'a' },
      { label: 'ACW', data: data.acw, backgroundColor: '#8b5cf6', stack: 'a' },
      { label: 'Not Ready', data: data.notReady, backgroundColor: '#ef4444', stack: 'a' },
      { label: 'Breaks', data: data.breaks, backgroundColor: '#ec4899', stack: 'a' },
    ],
  };
  return (
    <Box sx={{ height: 280, ...chartBox(dk) }}>
      <Bar
        data={cfg}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'top', labels: { color: tickColor(dk), boxWidth: 12, font: { size: 11 } } },
            datalabels: { display: false },
            tooltip: {
              ...ttStyle(dk),
              callbacks: { label: (ctx) => `${ctx.dataset.label}: ${fmtSeg(ctx.raw)}` },
            },
          },
          scales: {
            x: {
              stacked: true,
              ticks: { color: tickColor(dk), font: { size: 10 }, maxRotation: 45 },
              grid: { display: false },
            },
            y: {
              stacked: true,
              beginAtZero: true,
              ticks: { color: tickColor(dk), callback: (v) => fmtSeg(v) },
              grid: { color: gridColor(dk) },
            },
          },
        }}
      />
    </Box>
  );
};

/** Llamadas por intervalo – Grouped Bar */
const ChartLlamadas = ({ data, isDarkMode: dk }) => {
  if (!data) return null;
  const cfg = {
    labels: data.labels,
    datasets: [
      { label: 'Ent. Atendidas', data: data.entrantesAtendidas, backgroundColor: '#22c55e', borderRadius: 3 },
      { label: 'Ent. Abandonadas', data: data.entrantesAbandonadas, backgroundColor: '#ef4444', borderRadius: 3 },
      { label: 'Sal. Atendidas', data: data.salientesAtendidas, backgroundColor: '#3b82f6', borderRadius: 3 },
      { label: 'Sal. No Atendidas', data: data.salientesNoAtendidas, backgroundColor: '#f59e0b', borderRadius: 3 },
    ],
  };
  return (
    <Box sx={{ height: 280, ...chartBox(dk) }}>
      <Bar
        data={cfg}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'top', labels: { color: tickColor(dk), boxWidth: 12, font: { size: 11 } } },
            datalabels: { display: false },
            tooltip: ttStyle(dk),
          },
          scales: {
            x: {
              ticks: { color: tickColor(dk), font: { size: 10 }, maxRotation: 45 },
              grid: { display: false },
            },
            y: {
              beginAtZero: true,
              ticks: { color: tickColor(dk), precision: 0 },
              grid: { color: gridColor(dk) },
            },
          },
        }}
      />
    </Box>
  );
};

/** Utilización – Line chart */
const ChartUtilizacion = ({ data, isDarkMode: dk }) => {
  if (!data) return null;
  const cfg = {
    labels: data.labels,
    datasets: [
      {
        label: 'Utilización %',
        data: data.utilizacion,
        borderColor: '#ec4899',
        backgroundColor: 'rgba(236,72,153,0.1)',
        fill: true,
        tension: 0.3,
        pointRadius: 2,
      },
      {
        label: 'Connect Real %',
        data: data.connectReal,
        borderColor: '#3b82f6',
        backgroundColor: 'rgba(59,130,246,0.1)',
        fill: false,
        tension: 0.3,
        pointRadius: 2,
      },
      {
        label: 'Avail Real %',
        data: data.availReal,
        borderColor: '#22c55e',
        backgroundColor: 'rgba(34,197,94,0.1)',
        fill: false,
        tension: 0.3,
        pointRadius: 2,
      },
    ],
  };
  return (
    <Box sx={{ height: 280, ...chartBox(dk) }}>
      <Line
        data={cfg}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'top', labels: { color: tickColor(dk), boxWidth: 12, font: { size: 11 } } },
            datalabels: { display: false },
            tooltip: {
              ...ttStyle(dk),
              callbacks: { label: (ctx) => `${ctx.dataset.label}: ${ctx.raw}%` },
            },
          },
          scales: {
            x: {
              ticks: { color: tickColor(dk), font: { size: 10 }, maxRotation: 45 },
              grid: { display: false },
            },
            y: {
              beginAtZero: true,
              max: 100,
              ticks: { color: tickColor(dk), callback: (v) => `${v}%` },
              grid: { color: gridColor(dk) },
            },
          },
        }}
      />
    </Box>
  );
};

/** Agentes – Bar chart */
const ChartAgentes = ({ data, isDarkMode: dk }) => {
  if (!data) return null;
  const cfg = {
    labels: data.labels,
    datasets: [
      { label: 'Únicos', data: data.unicos, backgroundColor: '#06b6d4', borderRadius: 4 },
      { label: 'Simultáneos', data: data.simultaneos, backgroundColor: '#8b5cf6', borderRadius: 4 },
    ],
  };
  return (
    <Box sx={{ height: 260, ...chartBox(dk) }}>
      <Bar
        data={cfg}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'top', labels: { color: tickColor(dk), boxWidth: 12, font: { size: 11 } } },
            datalabels: { display: false },
            tooltip: ttStyle(dk),
          },
          scales: {
            x: {
              ticks: { color: tickColor(dk), font: { size: 10 }, maxRotation: 45 },
              grid: { display: false },
            },
            y: {
              beginAtZero: true,
              ticks: { color: tickColor(dk), precision: 0 },
              grid: { color: gridColor(dk) },
            },
          },
        }}
      />
    </Box>
  );
};

/** Distribución de Breaks – Doughnut */
const ChartBreaks = ({ labels, totals, isDarkMode: dk }) => {
  if (!labels || !totals) return null;
  // Filtrar breaks sin datos
  const filtered = labels.reduce((acc, l, i) => {
    if (totals[i] > 0) acc.push({ label: l, value: totals[i] });
    return acc;
  }, []);
  if (filtered.length === 0) return null;

  const colors = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316', '#10b981', '#6366f1'];
  const cfg = {
    labels: filtered.map((f) => f.label),
    datasets: [
      {
        data: filtered.map((f) => f.value),
        backgroundColor: filtered.map((_, i) => colors[i % colors.length]),
        borderWidth: 0,
      },
    ],
  };
  return (
    <Box sx={{ height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center', ...chartBox(dk) }}>
      <Doughnut
        data={cfg}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'right', labels: { color: tickColor(dk), font: { size: 11 }, boxWidth: 12 } },
            datalabels: {
              color: '#fff',
              font: { size: 11, weight: 'bold' },
              formatter: (v) => fmtSeg(v),
            },
            tooltip: {
              ...ttStyle(dk),
              callbacks: { label: (ctx) => `${ctx.label}: ${fmtSeg(ctx.raw)}` },
            },
          },
        }}
      />
    </Box>
  );
};

/** Tipificaciones – Doughnut */
const ChartTipificaciones = ({ data, isDarkMode: dk }) => {
  if (!data) return null;
  const entries = Object.entries(data).filter(([, v]) => v > 0);
  if (entries.length === 0) return null;

  const labelMap = { exitoso: 'Exitoso', noExitoso: 'No Exitoso', noEfectivo: 'No Efectivo', neutro: 'Neutro', otro: 'Otro' };
  const colorMap = { exitoso: '#22c55e', noExitoso: '#ef4444', noEfectivo: '#f59e0b', neutro: '#3b82f6', otro: '#8b5cf6' };
  const cfg = {
    labels: entries.map(([k]) => labelMap[k] || k),
    datasets: [
      {
        data: entries.map(([, v]) => v),
        backgroundColor: entries.map(([k]) => colorMap[k] || '#999'),
        borderWidth: 0,
      },
    ],
  };
  return (
    <Box sx={{ height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center', ...chartBox(dk) }}>
      <Doughnut
        data={cfg}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'right', labels: { color: tickColor(dk), font: { size: 11 }, boxWidth: 12 } },
            datalabels: {
              color: '#fff',
              font: { size: 11, weight: 'bold' },
              formatter: (v) => v.toLocaleString('es-AR'),
            },
            tooltip: ttStyle(dk),
          },
        }}
      />
    </Box>
  );
};

/* ========================================================================= */
/*  DASHBOARD PRINCIPAL                                                      */
/* ========================================================================= */

const DashboardAcuAgentes = ({ rawRows }) => {
  const theme = useTheme();
  const dk = theme.palette.mode === 'dark';

  // Inicializar filtros con todas las fechas seleccionadas
  const [filters, setFilters] = useState({
    fechas: [],
    fechaDesde: '',
    fechaHasta: '',
    horaDesde: 0,
    horaHasta: 24,
    minAgentes: 0,
    soloConActividad: false,
  });

  // Cuando cambian los rawRows, resetear fechas al total disponible
  useEffect(() => {
    if (!rawRows || rawRows.length === 0) return;
    const fechasSet = new Set();
    rawRows.forEach((r) => {
      const d = new Date(r.Intervalo);
      if (!isNaN(d)) fechasSet.add(d.toISOString().split('T')[0]);
    });
    const sorted = [...fechasSet].sort();
    setFilters((prev) => ({
      ...prev,
      fechas: sorted,
      fechaDesde: sorted[0] || '',
      fechaHasta: sorted[sorted.length - 1] || '',
    }));
  }, [rawRows]);

  // Aplicar filtros locales
  const filteredRows = useMemo(() => aplicarFiltros(rawRows, filters), [rawRows, filters]);

  const datos = useMemo(() => procesarDatosAcuAgentes(filteredRows), [filteredRows]);

  if (!datos) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
        <FilterBarAcuAgentes rawRows={rawRows} filters={filters} onChange={setFilters} />
        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          No hay datos para mostrar con los filtros actuales.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
      {/* Filtros locales */}
      <FilterBarAcuAgentes rawRows={rawRows} filters={filters} onChange={setFilters} />

      {/* KPI Cards */}
      <KpiCards kpis={datos.kpis} />

      <Divider />

      {/* Row 1: Distribución de Tiempo + Llamadas */}
      <Grid container spacing={2}>
        <Grid item xs={12} lg={6}>
          <SectionTitle isDarkMode={dk}>Distribución de Tiempo por Intervalo</SectionTitle>
          <ChartTiempo data={datos.chartTiempo} isDarkMode={dk} />
        </Grid>
        <Grid item xs={12} lg={6}>
          <SectionTitle isDarkMode={dk}>Llamadas por Intervalo</SectionTitle>
          <ChartLlamadas data={datos.chartLlamadas} isDarkMode={dk} />
        </Grid>
      </Grid>

      {/* Row 2: Utilización + Agentes */}
      <Grid container spacing={2}>
        <Grid item xs={12} lg={6}>
          <SectionTitle isDarkMode={dk}>Utilización por Intervalo (%)</SectionTitle>
          <ChartUtilizacion data={datos.chartUtilizacion} isDarkMode={dk} />
        </Grid>
        <Grid item xs={12} lg={6}>
          <SectionTitle isDarkMode={dk}>Agentes por Intervalo</SectionTitle>
          <ChartAgentes data={datos.chartAgentes} isDarkMode={dk} />
        </Grid>
      </Grid>

      {/* Row 3: Breaks + Tipificaciones */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <SectionTitle isDarkMode={dk}>Distribución de Breaks</SectionTitle>
          <ChartBreaks labels={datos.breakLabels} totals={datos.breakTotals} isDarkMode={dk} />
        </Grid>
        <Grid item xs={12} md={6}>
          <SectionTitle isDarkMode={dk}>Tipificaciones</SectionTitle>
          <ChartTipificaciones data={datos.tipificaciones} isDarkMode={dk} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardAcuAgentes;
